'use client'

import React, { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/utilities/ui'
import { SectionWrapper } from '@/components/SectionWrapper'
import SectionHeader from '@/components/ui/SectionHeader'
import MotionFadeIn from '@/components/ui/MotionFadeIn'
import { IconRenderer } from '@/fields/lucide-icon-picker/IconRenderer'
import { normalizeSectionSettings } from '@/utilities/sectionSettings'
import type { Job, Page } from '@/payload-types'

type AccordionBlockProps =
  | Extract<NonNullable<Page['careersLayout']>[number], { blockType: 'accordion' }>
  | Extract<NonNullable<Page['jobsLayout']>[number], { blockType: 'accordion' }>
  | Extract<NonNullable<Job['layout']>[number], { blockType: 'accordion' }>

export default function AccordionBlock({ header, items, settings }: AccordionBlockProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(0) // Always open first item by default

  const handleToggle = (index: number) => {
    setActiveIndex((prev) => (prev === index ? null : index))
  }

  const normalizedSettings = normalizeSectionSettings(settings)

  return (
    <SectionWrapper settings={normalizedSettings}>
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-6 items-start">
        <div className="w-full lg:w-2/5 lg:min-w-80">
          {header && (
            <SectionHeader
              infoTagText={header.infoTagText || ''}
              title={header.title}
              subtitle={header.subtitle || undefined}
              titleAs="h2"
              infoTagAs="h3"
              size="sm"
              className="mb-0"
            />
          )}
        </div>

        <div className="w-full lg:w-3/5 flex flex-col">
          {items?.map((item, index) => {
            const isOpen = activeIndex === index
            return (
              <MotionFadeIn key={`${item.title}-${index}`} delay={index * 0.04} className="w-full">
                <div className="border-b border-gray-300 border-solid py-4">
                  <button
                    onClick={() => handleToggle(index)}
                    className="w-full py-2 flex items-center justify-between text-left focus:outline-none hover:opacity-80 transition-opacity duration-200"
                    aria-expanded={isOpen}
                    aria-controls={`accordion-panel-${index}`}
                    id={`accordion-trigger-${index}`}
                  >
                    <div className="flex items-center gap-4 pr-4">
                      {item.icon && (
                        <div className="w-6 h-6 text-text-brand shrink-0">
                          <IconRenderer name={item.icon} size={24} />
                        </div>
                      )}
                      <span className="text-lg font-medium text-text-primary">{item.title}</span>
                    </div>
                    <ChevronDown
                      className={cn(
                        'h-5 w-5 text-text-primary transition-transform duration-300 ease-out shrink-0',
                        isOpen && 'rotate-180',
                      )}
                      aria-hidden
                    />
                  </button>

                  <div
                    id={`accordion-panel-${index}`}
                    role="region"
                    aria-labelledby={`accordion-trigger-${index}`}
                    className={cn(
                      'grid transition-[grid-template-rows] duration-500 ease-out',
                      isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                    )}
                  >
                    <div className="overflow-hidden">
                      <div className="py-3 pr-6">
                        <p className="body-lg text-text-secondary">{item.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </MotionFadeIn>
            )
          })}
        </div>
      </div>
    </SectionWrapper>
  )
}
